//#region imports
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Constant } from '../common/constant';
import { AuthenticationPath } from '../common/route-constant';
import { AppDataService } from 'app/modules/authentication/services/appdata.service';
import { AuthenticationService } from 'app/modules/authentication/services/authentication.service';
import { CommonService } from './common.service';
import { LoggedUser } from '../model/users/logged-user.modal';
//#endregion imports

@Injectable({
  providedIn: 'root',
})
export class AppInitializerService {
  constructor(
    private authService: AuthenticationService,
    private appDataService: AppDataService,
    private commonService: CommonService,
    private router: Router
  ) {}

  /**
   * Initialize the application by loading user data if token exists.
   * This is called via APP_INITIALIZER before the app bootstraps.
   */
  async initializeApp(): Promise<void> {
    const token = this.authService.getToken();

    // If no token exists, user is not logged in - nothing to initialize
    if (!token) {
      return;
    }

    // Show loader while fetching user data
    this.commonService.showLoader();

    try {
      // Fetch user profile details from API
      const userData: LoggedUser = await firstValueFrom(
        this.authService.getUserProfileDetails()
      );

      // Store user data in memory (AppDataService)
      this.appDataService.connectedUser = userData;

      // Set currency options from user's organization
      if (userData.organization) {
        this.appDataService.currencyOption = {
          currencySymbol: userData.organization.currency_symbol,
          displayCurrency: true,
          digits: '1.0-0',
          locale: userData.organization.locale,
          code: userData.organization.currency_symbol,
        };
      }

      // Restore current role from localStorage if available
      const savedRole = localStorage.getItem(Constant.LocalStorageKey.CurrentRole);
      if (savedRole) {
        this.appDataService.currentUserRole = JSON.parse(savedRole);
      } else if (userData.role && userData.role.length > 0) {
        // If no saved role, use the first role from user data
        const roles = [...userData.role].sort((a, b) => a.role_id - b.role_id);
        this.appDataService.currentUserRole = roles[0].role_name;
      }

      // Fetch user permissions
      if (userData.user_id) {
        try {
          const permissions = await firstValueFrom(
            this.commonService.getUserPermissionById(userData.user_id)
          );
          this.appDataService.userPermission = permissions;
        } catch (permError) {
          // Permission fetch failure is non-critical, continue without permissions
          console.warn('Failed to fetch user permissions:', permError);
        }
      }
    } catch (error) {
      // On any error (401, 403, network error), clear data and redirect to login
      console.error('Failed to load user profile:', error);
      this.clearAndRedirectToLogin();
    } finally {
      this.commonService.hideLoader();
    }
  }

  /**
   * Clear all user data and redirect to login page
   */
  private clearAndRedirectToLogin(): void {
    // Clear only the items we manage (token and currentRole)
    localStorage.removeItem(Constant.LocalStorageKey.Token);
    localStorage.removeItem(Constant.LocalStorageKey.CurrentRole);

    // Clear memory-based data
    this.appDataService.clearUserData();

    // Redirect to login
    this.router.navigate([`/${AuthenticationPath.Login}`]);
  }
}
